import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserCheck, Plus, Search, Phone, Trash2, Edit, Camera, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import Navigation from '../components/Navigation';
import { getAdminHeaders, canEdit } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AREA_LABELS = {
  louvor: 'Louvor',
  midia: 'Mídia',
  lider_gf: 'Lider GF',
  checkin: 'Check-in',
  projeto_semente: 'Proj. Semente',
  gincana: 'Gincana',
  administracao: 'Administração',
  intercessao: 'Intercessão',
  lider_ministerio: 'Líder Min.',
  pregador: 'Pregador',
};

const ListaVoluntarios = () => {
  const navigate = useNavigate();
  const [volunteers, setVolunteers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterArea, setFilterArea] = useState('');

  useEffect(() => { fetchVolunteers(); }, []);

  const fetchVolunteers = async () => {
    try {
      const res = await axios.get(`${API}/volunteers`);
      setVolunteers(res.data);
    } catch { toast.error('Erro ao carregar voluntarios'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id, nome) => {
    try {
      await axios.delete(`${API}/volunteers/${id}`, { headers: getAdminHeaders() });
      toast.success(`${nome} removido!`);
      fetchVolunteers();
    } catch { toast.error('Erro ao excluir'); }
  };

  const filtered = volunteers.filter((v) => {
    const matchSearch = v.nome.toLowerCase().includes(search.toLowerCase());
    const matchArea = !filterArea || (v.areas && v.areas.includes(filterArea));
    return matchSearch && matchArea;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <p className="font-inter text-gray-500">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <UserCheck className="w-7 h-7" strokeWidth={2.5} />
              <h1 className="font-nunito font-bold text-3xl">Voluntários</h1>
            </div>
            {canEdit() && (
              <button
                data-testid="btn-add-volunteer"
                onClick={() => navigate('/voluntarios/cadastro')}
                className="bg-white/10 hover:bg-white/20 text-white rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2 transition-all"
              >
                <Plus className="w-4 h-4" /> Novo
              </button>
            )}
          </div>
          <p className="font-inter text-white/70 text-sm">{volunteers.length} voluntarios cadastrados</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Search & Filter */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="search-volunteer"
              placeholder="Buscar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-xl border-gray-200 bg-white"
            />
          </div>
          <select
            data-testid="filter-area"
            value={filterArea}
            onChange={(e) => setFilterArea(e.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 text-sm"
          >
            <option value="">Todas as areas</option>
            {Object.entries(AREA_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>

        {/* List */}
        <div className="space-y-3">
          {filtered.map((v) => (
            <Card key={v.id} className="bg-white rounded-xl p-4 shadow-sm border border-[#E1ECE5] hover:shadow-md transition-all">
              <div className="flex items-center gap-4">
                {v.foto_url ? (
                  <img src={`${BACKEND_URL}${v.foto_url}`} alt={v.nome} className="w-12 h-12 rounded-full object-cover border-2 border-[#E1ECE5]" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#F2F7F4] flex items-center justify-center border-2 border-[#E1ECE5]">
                    <Camera className="w-5 h-5 text-[#1A4D2E]/40" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="font-nunito font-bold text-lg text-[#1A4D2E] truncate">{v.nome}</h4>
                  <p className="font-inter text-sm text-gray-500 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {v.celular}
                  </p>
                  {v.email && (
                    <p className="font-inter text-xs text-gray-400">{v.email}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(v.areas || []).map((a) => (
                      <span key={a} className={`px-2 py-0.5 rounded text-xs font-semibold ${
                        (v.lider_areas || []).includes(a)
                          ? 'bg-amber-100 text-amber-700 border border-amber-200'
                          : 'bg-[#F2F7F4] text-[#1A4D2E]'
                      }`}>
                        {(v.lider_areas || []).includes(a) ? '★ ' : ''}{AREA_LABELS[a] || a}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="text-xs font-semibold text-gray-400 capitalize">{v.nivel_acesso === 'admin' ? 'Administrador' : 'Voluntario'}</span>
                  {canEdit() && (
                    <div className="flex gap-1">
                      <button data-testid={`edit-vol-${v.id}`} onClick={() => navigate(`/voluntarios/cadastro?edit=${v.id}`)} className="text-[#1A4D2E] hover:bg-[#1A4D2E]/10 rounded-full p-1.5 transition-all">
                        <Edit className="w-4 h-4" />
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`delete-vol-${v.id}`} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full p-1.5 transition-all">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir voluntario</AlertDialogTitle>
                            <AlertDialogDescription>Tem certeza que deseja excluir <strong>{v.nome}</strong>?</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(v.id, v.nome)} className="bg-red-500 hover:bg-red-600">Excluir</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
          {filtered.length === 0 && (
            <Card className="bg-white rounded-2xl p-12 text-center shadow-md border border-[#E1ECE5]">
              <UserCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="font-inter text-gray-500">{search || filterArea ? 'Nenhum voluntario encontrado' : 'Nenhum voluntario cadastrado'}</p>
            </Card>
          )}
        </div>
      </div>
      <Navigation active="voluntarios" />
    </div>
  );
};

export default ListaVoluntarios;
